// ph-acid.js
import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Kalkulator pH Larutan Asam - Hitung pH dengan Akurat";
const DESCRIPTION =
  "Kalkulator pH untuk asam dan basa. Hitung derajat keasaman dengan mudah menggunakan konsentrasi molar. Alat akurat untuk kimiawan dan pelajar dengan penjelasan rumus dan contoh.";
const KEYWORDS =
  "hitung pH, kalkulator pH, derajat keasaman, hitung pH asam basa, konsentrasi ion hidrogen, rumus pH, pH calculator indonesia";
const OG_URL = "https://do-calculate.com/calculator/id/ph-acid/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator pH",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "ScienceApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator Sains",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Kalkulator pH",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Apa itu pH?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "pH adalah ukuran derajat keasaman atau kebasaan larutan air, biasanya berkisar antara 0 hingga 14, di mana 7 netral, di bawah 7 asam, dan di atas 7 basa.",
        },
      },
      {
        "@type": "Question",
        name: "Bagaimana cara menghitung pH?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "pH dihitung menggunakan logaritma negatif dari konsentrasi ion hidrogen dalam larutan, dengan rumus: pH = -log[H+].",
        },
      },
    ],
  },
];

function PHCalculator() {
  const [calcType, setCalcType] = useState("ph_from_h");
  const [inputValue, setInputValue] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculatePH = () => {
    try {
      setError("");
      const val = parseFloat(inputValue);

      if (isNaN(val) || val <= 0) {
        if (inputValue !== "") {
          setError("Harap masukkan nilai positif yang valid lebih besar dari nol");
        }
        setResult(null);
        return;
      }

      let res = {};
      switch (calcType) {
        case "ph_from_h":
          const ph = -Math.log10(val);
          const pohFromH = 14 - ph;
          res = {
            ph: ph.toFixed(4),
            poh: pohFromH.toFixed(4),
            h_conc: val,
            oh_conc: (Math.pow(10, -14) / val).toExponential(4),
            status: ph < 7 ? "Asam" : ph > 7 ? "Basa" : "Netral",
          };
          break;
        case "h_from_ph":
          const hConc = Math.pow(10, -val);
          const pohFromPh = 14 - val;
          res = {
            ph: val.toFixed(4),
            poh: pohFromPh.toFixed(4),
            h_conc: hConc.toExponential(4),
            oh_conc: (Math.pow(10, -14) / hConc).toExponential(4),
            status: val < 7 ? "Asam" : val > 7 ? "Basa" : "Netral",
          };
          break;
        default:
          break;
      }
      setResult(res);
    } catch (err) {
      setError("Kesalahan dalam proses perhitungan");
      setResult(null);
    }
  };

  useEffect(() => {
    calculatePH();
  }, [calcType, inputValue]);

  const handleClear = () => {
    setInputValue("");
    setResult(null);
    setError("");
  };

  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator pH Larutan Asam</h1>
          </div>

          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                Gunakan <strong>Kalkulator pH</strong> untuk mengkonversi konsentrasi ion hidrogen menjadi nilai pH atau sebaliknya. Alat ini penting untuk siswa kimia dan profesional untuk menentukan derajat keasaman atau kebasaan larutan air dengan akurasi tinggi.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Masukkan Data untuk Perhitungan</h2>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  <label >Jenis Perhitungan</label>
                  <select
                    className="form-select"
                    value={calcType}
                    onChange={(e) => {
                      setCalcType(e.target.value);
                      setInputValue("");
                    }}
                  >
                    <option value="ph_from_h">Hitung pH dari konsentrasi [H+]</option>
                    <option value="h_from_ph">Hitung [H+] dari nilai pH</option>
                  </select>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-12">
                  <label >
                    {calcType === "ph_from_h" ? "Konsentrasi Ion Hidrogen [H+] (mol/L)" : "Nilai pH"}
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={calcType === "ph_from_h" ? "Contoh: 0,001" : "Contoh: 7"}
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ padding: "10px 30px" }}>
                  Hapus
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Peringatan:</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Hasil Akhir:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Skala</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Nilai</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>pH</td>
                          <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#1a5f3f" }}>
                            {result.ph}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>pOH</td>
                          <td style={{ textAlign: "center" }}>{result.poh}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Status Larutan</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.status}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Konsentrasi Molar</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>🧪 <strong>Konsentrasi [H+]:</strong> {result.h_conc} mol/L</li>
                          <li>🧪 <strong>Konsentrasi [OH-]:</strong> {result.oh_conc} mol/L</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>

        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Apa itu pH?</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  pH adalah logaritma konsentrasi ion hidrogen dalam larutan, dan digunakan untuk menyatakan derajat keasaman atau kebasaan suatu zat. Nilainya berkisar dari 0 hingga 14.
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Larutan Asam:</strong> pH kurang dari 7.</li>
                  <li><strong>Larutan Netral:</strong> seperti air murni, pH = 7.</li>
                  <li><strong>Larutan Basa (Alkali):</strong> pH lebih dari 7.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Cara Menghitung pH</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Perhitungan kimia untuk pH didasarkan pada rumus berikut:
                </p>
                <div className="alert alert-info" style={{ direction: "ltr", textAlign: "left" }}>
                  1. pH = -log₁₀[H⁺]<br />
                  2. pOH = -log₁₀[OH⁻]<br />
                  3. pH + pOH = 14 (pada suhu 25°C)
                </div>
                <p style={{ lineHeight: "1.8" }}>
                  di mana [H⁺] adalah konsentrasi ion hidrogen dalam mol per liter, dan [OH⁻] adalah konsentrasi ion hidroksida.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Pentingnya Pengukuran pH dalam Kehidupan Kita</strong>
              </div>
              <div className="info-section-body">
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Dalam Kedokteran:</strong> Menjaga pH darah pada tingkat 7,4 sangat penting untuk kehidupan.</li>
                  <li><strong>Dalam Pertanian:</strong> Keasaman tanah secara langsung mempengaruhi pertumbuhan tanaman dan penyerapan nutrisi.</li>
                  <li><strong>Dalam Industri:</strong> Pabrik pengolahan air dan industri makanan memerlukan pemantauan pH yang ketat.</li>
                  <li><strong>Dalam Kolam Renang:</strong> Untuk memastikan efektivitas klorin dan keamanan kulit perenang.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Contoh Praktis Perhitungan pH</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px" }}>Contoh 1: Menghitung pH Asam Klorida</h3>
                <p>Jika konsentrasi ion hidrogen dalam suatu larutan adalah 0,01 mol/L, berapakah pH-nya?</p>
                <p>Penyelesaian: pH = -log(0,01) = 2. Larutan bersifat asam kuat.</p>

                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>Contoh 2: Menghitung Konsentrasi dari pH</h3>
                <p>Jika pH suatu larutan adalah 10, berapakah konsentrasi ion hidrogennya?</p>
                <p>Penyelesaian: [H⁺] = 10^-10 mol/L. Larutan bersifat basa.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Tips Penggunaan yang Benar</strong>
              </div>
              <div className="info-section-body">
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Pastikan memasukkan konsentrasi dengan satuan yang benar (mol/L).</li>
                  <li>Dalam kasus asam lemah, konstanta disosiasi (Ka) harus digunakan sebelum menghitung konsentrasi akhir.</li>
                  <li>Ingat bahwa skala pH adalah logaritmik, artinya perubahan satu derajat pH berarti perubahan konsentrasi sebesar 10 kali lipat.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Pertanyaan Umum (FAQ)</strong>
              </div>
              <div className="info-section-body">
                <div className="mb-3">
                  <strong>1. Bisakah nilai pH kurang dari nol?</strong>
                  <p>Ya, dalam kasus asam yang sangat pekat dan kuat, nilainya bisa negatif, meskipun skala umumnya adalah 0 hingga 14.</p>
                </div>
                <div className="mb-3">
                  <strong>2. Apa alat yang digunakan untuk mengukur pH?</strong>
                  <p>Alat yang disebut "pH meter" atau kertas lakmus dan indikator warna digunakan untuk memperkirakan nilai dengan cepat.</p>
                </div>
                <div className="mb-3">
                  <strong>3. Bagaimana pengaruh pengenceran terhadap pH?</strong>
                  <p>Saat mengencerkan asam dengan air, konsentrasi ion hidrogen berkurang sehingga nilai pH meningkat mendekati angka 7.</p>
                </div>
                <div className="mb-3">
                  <strong>4. Berapa nilai pH air suling?</strong>
                  <p>Nilai pH air suling murni adalah 7, sehingga netral, tetapi paparan udara dapat membuatnya sedikit asam karena pelarutan karbon dioksida.</p>
                </div>
                <div className="mb-3">
                  <strong>5. Mengapa pH darah relatif konstan?</strong>
                  <p>Tubuh manusia memiliki sistem "larutan penyangga" yang menjaga stabilitas pH darah dalam rentang yang sangat sempit.</p>
                </div>
                <div className="mb-3">
                  <strong>6. Bagaimana cara menghitung pOH dari pH?</strong>
                  <p>Cukup kurangi nilai pH dari angka 14, jadi jika pH sama dengan 4 maka pOH sama dengan 10.</p>
                </div>
                <div className="mb-3">
                  <strong>7. Apakah suhu mempengaruhi pengukuran?</strong>
                  <p>Ya, nilai pH dipengaruhi oleh suhu karena disosiasi air berubah seiring suhu, jadi sebaiknya diukur pada suhu 25°C.</p>
                </div>
                <div className="mb-3">
                  <strong>8. Apa perbedaan antara asam kuat dan asam lemah?</strong>
                  <p>Asam kuat terionisasi sempurna dalam air menghasilkan semua ion hidrogennya, sedangkan asam lemah hanya terionisasi sebagian.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PHCalculator;